import type { TFunction } from 'i18next';
import { MailIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';

import { SIMULATION_NAME_MAX } from '@/shared/types/simulation-draft';
import { truncateWholeChars } from '@/util/checkNames';

import type {
  EvaluationStatus,
  ScheduleCadence,
  ScheduleFrequency,
  Simulation,
  SimulationChannelType,
} from './types';

export const CHANNEL_ICONS: Record<SimulationChannelType, typeof MessageSquareIcon> = {
  chat: MessageSquareIcon,
  email: MailIcon,
  voice: PhoneIcon,
};

export const isEvaluationInProgress = (status?: EvaluationStatus) =>
  status === 'pending' || status === 'running';

/** Suffixes a drafted name that is taken, so an auto-filled name never errors on uniqueness. */
export const uniqueSimulationName = (
  name: string,
  simulations: Pick<Simulation, 'name'>[]
): string => {
  const taken = new Set(simulations.map((s) => s.name.trim().toLowerCase()));
  if (!name || !taken.has(name.toLowerCase())) {
    return name;
  }
  for (let suffix = 2; ; suffix += 1) {
    const tail = ` ${suffix}`;
    const candidate = `${truncateWholeChars(name, SIMULATION_NAME_MAX - tail.length)}${tail}`;
    if (!taken.has(candidate.toLowerCase())) {
      return candidate;
    }
  }
};

/** Distinct simulation categories, alphabetically sorted with `null` (uncategorized) last. */
export const listCategories = (simulations: Pick<Simulation, 'category'>[]): (string | null)[] => {
  const set = new Set<string | null>();
  simulations.forEach((s) => set.add(s.category));
  return Array.from(set).sort((a, b) => {
    if (a === null) {
      return 1;
    }
    if (b === null) {
      return -1;
    }
    return a.localeCompare(b);
  });
};

export const formatDurationSeconds = (seconds: number | null): string => {
  if (seconds === null) {
    return '-';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
};

export const formatDurationMs = (startedAt: string | null, completedAt: string | null): string => {
  if (!startedAt || !completedAt) {
    return '-';
  }
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  return formatDurationSeconds(Math.floor(ms / 1000));
};

// --- Cron helpers ---

export interface CronFormValues {
  frequency: ScheduleFrequency;
  hour: number;
  minute: number;
  dayOfWeek?: number; // 1=MON .. 7=SUN
  dayOfMonth?: number; // 1-28
  raw?: string; // only for custom
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Reverse-parse a cron expression into friendly form values.
 * Returns frequency: 'custom' for expressions that don't match known patterns.
 */
export const parseCronToForm = (cron: string): CronFormValues => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return { frequency: 'custom', hour: 0, minute: 0, raw: cron };
  }

  const [minuteStr, hourStr, dayOfMonthStr, monthStr, dayOfWeekStr] = parts;
  const minute = Number(minuteStr);

  if (isNaN(minute)) {
    return { frequency: 'custom', hour: 0, minute: 0, raw: cron };
  }

  // hourly: M * * * *
  if (hourStr === '*' && dayOfMonthStr === '*' && monthStr === '*' && dayOfWeekStr === '*') {
    return { frequency: 'hourly', hour: 0, minute };
  }

  const hour = Number(hourStr);
  if (isNaN(hour)) {
    return { frequency: 'custom', hour: 0, minute: 0, raw: cron };
  }

  // daily: M H * * *
  if (dayOfMonthStr === '*' && monthStr === '*' && dayOfWeekStr === '*') {
    return { frequency: 'daily', hour, minute };
  }

  // weekly: M H * * DOW
  if (dayOfMonthStr === '*' && monthStr === '*' && dayOfWeekStr !== '*') {
    const dow = Number(dayOfWeekStr);
    if (!isNaN(dow) && dow >= 0 && dow <= 7) {
      return { frequency: 'weekly', hour, minute, dayOfWeek: dow };
    }
  }

  // monthly: M H DOM * *
  if (dayOfMonthStr !== '*' && monthStr === '*' && dayOfWeekStr === '*') {
    const dom = Number(dayOfMonthStr);
    if (!isNaN(dom) && dom >= 1 && dom <= 28) {
      return { frequency: 'monthly', hour, minute, dayOfMonth: dom };
    }
  }

  return { frequency: 'custom', hour, minute, raw: cron };
};

const DAY_OF_WEEK_KEYS: Record<number, string> = {
  0: 'business_hours.frequency.sunday',
  1: 'business_hours.frequency.monday',
  2: 'business_hours.frequency.tuesday',
  3: 'business_hours.frequency.wednesday',
  4: 'business_hours.frequency.thursday',
  5: 'business_hours.frequency.friday',
  6: 'business_hours.frequency.saturday',
  7: 'business_hours.frequency.sunday',
};

// Managed "weekdays" preset from orchestrator: `M H * * 1-5` (Mon–Fri at H:M UTC).
const WEEKDAYS_CRON_REGEX = /^(\d+)\s+(\d+)\s+\*\s+\*\s+1-5$/;

/**
 * Map a cron expression to one of the managed `ScheduleCadence` values, or
 * `null` for hourly/daily/custom expressions that don't fit those buckets.
 */
export const cronToCadence = (cron: string): ScheduleCadence | null => {
  const trimmed = cron.trim();
  if (WEEKDAYS_CRON_REGEX.test(trimmed)) {
    return 'weekdays';
  }
  const form = parseCronToForm(trimmed);
  if (form.frequency === 'weekly') {
    return 'weekly';
  }
  if (form.frequency === 'monthly') {
    return 'monthly';
  }
  return null;
};

/**
 * Returns a human-readable description of a cron expression.
 */
export const describeCron = (cron: string, t: TFunction): string => {
  const weekdaysMatch = cron.trim().match(WEEKDAYS_CRON_REGEX);
  if (weekdaysMatch) {
    const [, minuteStr, hourStr] = weekdaysMatch;
    const weekdaysTime = `${pad(Number(hourStr))}:${pad(Number(minuteStr))} UTC`;
    return t('simulations.schedule_desc_weekdays', { 0: weekdaysTime });
  }

  const form = parseCronToForm(cron);
  const time = `${pad(form.hour)}:${pad(form.minute)} UTC`;

  if (form.frequency === 'hourly') {
    return t('simulations.schedule_desc_hourly', { 0: pad(form.minute) });
  }
  if (form.frequency === 'daily') {
    return t('simulations.schedule_desc_daily', { 0: time });
  }
  if (form.frequency === 'weekly' && form.dayOfWeek != null) {
    const dayKey = DAY_OF_WEEK_KEYS[form.dayOfWeek] ?? 'common.monday';
    return t('simulations.schedule_desc_weekly', { 0: t(dayKey), 1: time });
  }
  if (form.frequency === 'monthly' && form.dayOfMonth != null) {
    return t('simulations.schedule_desc_monthly', { 0: form.dayOfMonth, 1: time });
  }
  return cron;
};
